/* Hand-drawn loop, drawn like a real pen: one counter-clockwise oval that starts at the top,
   finishes with a small flick just past its start (no long overlap), and gets thicker on the
   right like pen pressure. Built at the loop's real pixel size (never stretched), revealed by an
   animated mask, and nudged at random so no two loops match. A loop is redrawn only when its
   size/color goes from unselected to selected. */
(() => {
  const NS = 'http://www.w3.org/2000/svg';
  const rnd = (m) => (Math.random() * 2 - 1) * m;
  let uid = 0;

  // centerline in a unit box: start top-centre, round the left, bottom, up the right, flick at the top
  const BASE = [
    [0.58, 0.07],
    [0.38, 0.03], [0.10, 0.16], [0.04, 0.47],
    [-0.01, 0.78], [0.22, 0.99], [0.50, 0.98],
    [0.80, 0.97], [1.00, 0.76], [0.98, 0.46],
    [0.96, 0.20], [0.84, 0.05], [0.70, 0.00]
  ];
  // pen pressure along the stroke (0 = start, 1 = end): light start, heavy right side, lifted end
  const PRESSURE = [[0, .35], [.08, .7], [.3, .9], [.5, 1.1], [.68, 1.55], [.85, 1.7], [.95, 1.3], [1, .55]];
  // the "added" tick: short stroke down to the right, then one long confident flick up; same pen
  const TICK = [
    [0.00, 0.55],
    [0.08, 0.68], [0.20, 0.86], [0.34, 0.98],
    [0.50, 0.72], [0.78, 0.30], [1.00, 0.02]
  ];
  const TICK_PRESSURE = [[0, .5], [.12, .9], [.3, 1.5], [.36, 1.6], [.6, 1.2], [.9, .8], [1, .4]];
  // product-page picks (owner's reference): light start along the top, thin left side, heavy up the
  // right, and the pen comes down hardest at the end, flicking up and out past the start
  const PICK = [
    [0.62, 0.05],
    [0.40, 0.00], [0.08, 0.12], [0.03, 0.48],
    [0.00, 0.82], [0.26, 1.00], [0.52, 0.98],
    [0.82, 0.96], [0.99, 0.76], [0.98, 0.46],
    [0.97, 0.20], [0.86, 0.02], [0.74, -0.06]
  ];
  const PICK_PRESSURE = [[0, .3], [.12, .55], [.3, .7], [.5, 1], [.66, 1.45], [.8, 1.75], [.92, 1.9], [1, 1.6]];
  // wide picks (style chips like "vintage heavyweight $35", Oct 21): the round PICK squashed into a 7:1 box pinched to a
  // hairline at the ends and its flick turned into a hook. This is drawn for the shape instead: long, slightly bowed
  // sides, tight rounded ends, and the pen crossing just past where it started.
  const WIDE = [
    [0.80, 0.10],
    [0.60, 0.00], [0.28, 0.01], [0.10, 0.13],
    [-0.01, 0.24], [-0.01, 0.80], [0.13, 0.90],
    [0.33, 1.02], [0.68, 1.00], [0.88, 0.88],
    [1.01, 0.79], [1.01, 0.26], [0.90, 0.15],
    [0.82, 0.05], [0.74, -0.03], [0.64, -0.12]
  ];
  const WIDE_PRESSURE = [[0, .35], [.1, .75], [.3, .95], [.5, 1.15], [.65, 1.35], [.8, 1.55], [.92, 1.2], [1, .45]];
  const pressure = (s, table) => {
    table = table || PRESSURE;
    for (let i = 1; i < table.length; i++) {
      const [s1, v1] = table[i], [s0, v0] = table[i - 1];
      if (s <= s1) return v0 + (v1 - v0) * (s - s0) / (s1 - s0);
    }
    return table[table.length - 1][1];
  };
  const bez = (a, b, c, d, t) => {
    const u = 1 - t;
    return [0, 1].map((k) => u * u * u * a[k] + 3 * u * u * t * b[k] + 3 * u * t * t * c[k] + t * t * t * d[k]);
  };
  const f = (n) => n.toFixed(2);

  const geometry = (w, h, base, ptable, thin) => {
    base = base || BASE;
    const k = Math.min(Math.max(Math.min(w, h) / 28, 0.9), 1.9) * (thin || 1); // thicker ink on big loops, capped
    const hwMax = 1.05 * k;
    const pad = hwMax + 0.5;
    const jx = Math.min(0.03, 6 / w), jy = Math.min(0.035, 1.6 / h);           // wobble in px, not box fractions
    const cp = base.map(([x, y], i) => [x + (i ? rnd(jx) : 0), y + (i ? rnd(jy) : 0)]);
    const toPx = ([x, y]) => [pad + x * (w - 2 * pad), pad + y * (h - 2 * pad)];
    // sample the four curves
    const pts = [];
    for (let s = 0; s < (base.length - 1) / 3; s++) {
      const [a, b, c, d] = [cp[s * 3], cp[s * 3 + 1], cp[s * 3 + 2], cp[s * 3 + 3]].map(toPx);
      for (let i = s ? 1 : 0; i <= 24; i++) pts.push(bez(a, b, c, d, i / 24));
    }
    const len = [0];
    for (let i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    const total = len[len.length - 1];
    const weight = 1 + rnd(0.1);
    const L = [], R = [];
    pts.forEach((p, i) => {
      const a = pts[Math.max(i - 1, 0)], b = pts[Math.min(i + 1, pts.length - 1)];
      const tx = b[0] - a[0], ty = b[1] - a[1], tl = Math.hypot(tx, ty) || 1;
      const hw = 0.5 * k * pressure(len[i] / total, ptable) * weight;
      L.push([p[0] - ty / tl * hw, p[1] + tx / tl * hw]);
      R.push([p[0] + ty / tl * hw, p[1] - tx / tl * hw]);
    });
    const endHw = 0.5 * k * pressure(1, ptable) * weight, startHw = 0.5 * k * pressure(0, ptable) * weight;
    const ink = 'M' + L.map((p) => f(p[0]) + ' ' + f(p[1])).join('L')
      + `A${f(endHw)} ${f(endHw)} 0 0 1 ` + R.slice(-1).map((p) => f(p[0]) + ' ' + f(p[1]))
      + 'L' + R.reverse().map((p) => f(p[0]) + ' ' + f(p[1])).join('L')
      + `A${f(startHw)} ${f(startHw)} 0 0 1 ${f(L[0][0])} ${f(L[0][1])}Z`;
    const line = 'M' + pts.map((p) => f(p[0]) + ' ' + f(p[1])).join('L');
    return { ink, line, reveal: hwMax * 2.2 + 1 };
  };

  const draw = (svg) => {
    const box = svg.getBoundingClientRect();
    if (box.width < 4 || box.height < 4) return false; // hidden: drawn when it's shown
    const w = box.width, h = box.height;
    const tick = svg.classList.contains('aa-tick');
    const pencil = !tick && !!svg.closest('.aa-pdp');
    const wide = !tick && w / h > 2.6;
    const g = tick ? geometry(w, h, TICK, TICK_PRESSURE)
      : wide ? geometry(w, h, WIDE, WIDE_PRESSURE, pencil ? 1.5 : 1.1)
      : pencil ? geometry(w, h, PICK, PICK_PRESSURE, 1.6) : geometry(w, h);
    const raw = svg.dataset.color || svg.querySelector('path')?.getAttribute('stroke') || 'currentColor';
    const color = /^[#\w(),.%\s-]+$/.test(raw) ? raw : 'currentColor'; // it's written into markup below
    svg.dataset.color = color;
    svg.setAttribute('viewBox', `0 0 ${f(w)} ${f(h)}`);
    svg.removeAttribute('preserveAspectRatio');
    const id = svg.dataset.maskId || (svg.dataset.maskId = 'aa-loop-m' + (++uid));
    // graphite: noise knocks small gaps out of the stroke (paper tooth) and roughens its edges slightly
    // rough ink (the owner's reference): solid stroke, pen pressure, jagged crisp edges, no speckle inside
    const grain = pencil ? `<filter id="${id}g" x="-20%" y="-20%" width="140%" height="140%" color-interpolation-filters="sRGB">`
      + `<feTurbulence type="fractalNoise" baseFrequency="1.6" numOctaves="1" seed="${Math.floor(Math.random() * 999)}" result="n"/>`
      + `<feDisplacementMap in="SourceGraphic" in2="n" scale="${wide ? 1.1 : 1.9}" xChannelSelector="R" yChannelSelector="G" result="rough"/>`
      // wide loops: a soft alpha ramp instead of the hard cut, so long near-flat strokes don't stair-step
      + `<feComponentTransfer in="rough"><feFuncA type="${wide ? 'table' : 'discrete'}" tableValues="${wide ? '0 0.2 1 1' : '0 1 1'}"/></feComponentTransfer></filter>` : '';
    svg.innerHTML = grain + `<mask id="${id}" maskUnits="userSpaceOnUse" x="-4" y="-4" width="${f(w + 8)}" height="${f(h + 8)}">`
      + `<path class="aa-loop__reveal" pathLength="100" d="${g.line}" fill="none" stroke="#fff" stroke-width="${f(g.reveal)}" stroke-linecap="round" stroke-linejoin="round"/></mask>`
      + (pencil
        ? `<g mask="url(#${id})"><path class="aa-loop__ink" d="${g.ink}" fill="${color}" filter="url(#${id}g)"/></g>`
        : `<path class="aa-loop__ink" d="${g.ink}" fill="${color}" stroke="${color}" stroke-width=".3" stroke-linejoin="round" mask="url(#${id})"/>`);
    svg.dataset.drawn = `${Math.round(w)}x${Math.round(h)}`;
    return true;
  };

  // draw (or redraw, replaying the animation) a tick into an empty <svg class="aa-loop aa-tick">
  window.aaTick = (svg) => { svg.classList.remove('aa-loop--still'); return draw(svg); };

  const all = (root) => (root || document).querySelectorAll('.aa-loop').forEach(draw);
  all();
  document.addEventListener('shopify:section:load', (e) => all(e.target));

  // new loop only when a chip/swatch goes from unselected to selected; the product JS re-sets
  // aria-pressed="true" on already-selected options on every click, and those keep their loop
  new MutationObserver((ms) => ms.forEach((m) => {
    if (m.oldValue !== 'true' && m.target.getAttribute('aria-pressed') === 'true') m.target.querySelectorAll('.aa-loop').forEach((svg) => { svg.classList.remove('aa-loop--still'); draw(svg); });
  })).observe(document.body, { attributes: true, attributeFilter: ['aria-pressed'], attributeOldValue: true, subtree: true });

  // a loop whose box changed size (resize, font swap) is redrawn without replaying the animation
  let t;
  const redrawResized = () => document.querySelectorAll('.aa-loop[data-drawn]').forEach((svg) => {
    const b = svg.getBoundingClientRect();
    if (b.width < 4 || `${Math.round(b.width)}x${Math.round(b.height)}` === svg.dataset.drawn) return;
    svg.classList.add('aa-loop--still');
    draw(svg);
  });
  window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(redrawResized, 200); });
  if (document.fonts) document.fonts.ready.then(redrawResized);
})();
